<template>
  <div>
    <div class="visible pt-6">
      <h1 v-if="!loading" class="pb-2 text-lg font-bold leading-tight xl:text-2xl xl:pb-4">
        {{ $t('components.payment.title') }}
      </h1>

      <Loading v-if="loading && !errors.length">
        <p v-if="paymentProcessing" class="max-w-sm pt-6 mx-auto text-center xl:pt-12">
          {{ $t('components.payment.loading', brandInfo) }}
        </p>
      </Loading>

      <div v-if="errors.length" ref="error" class="flex w-full p-4 mt-2 mb-6 border border-sn-secondary-700 bg-sn-secondary-100">
        <div class="flex-shrink-0 pt-px mr-2">
          <svg viewBox="0 0 195 195" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'w-4 fill-current text-sn-secondary-700'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M97.5 195c53.8 0 97.5-43.7 97.5-97.5S151.3 0 97.5 0 0 43.7 0 97.5 43.7 195 97.5 195zM44.4 65.6l21.2-21.2 31.9 31.9 31.9-31.9 21.2 21.2-31.9 31.9 31.9 31.9-21.2 21.2-31.9-31.9-31.9 31.9-21.2-21.2 31.9-31.9z"/></svg>
        </div>
        <div>
          <p class="pb-2 font-bold leading-none xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <div v-if="!loading">
        <div
          v-if="!innerUltimateFailure"
          class="w-full p-4 mb-10 space-y-4 bg-white border border-sn-gray-200"
        >
          <div class="leading-tight">
            {{ $t('components.payment.description') }}
          </div>

          <div class="space-y-4" :class="[formReady ? 'visible' : 'hidden']">
            <div>
              <FormInput
                id="name"
                v-model.trim="fields.name.value"
                :label="$t('components.payment.form.name.label')"
                :valid="fields.name.valid"
                :error-string="$t('components.payment.error.name')"
                @focus="resetFieldValidity('name')"
              />
            </div>

            <div class="w-full">
              <div
                class="w-full input-component"
                :class="{
                  'is-active': fields.cardNumber.focused || fields.cardNumber.filled,
                  'is-error': !fields.cardNumber.valid,
                }"
              >
                <label>
                  {{ $t('components.payment.form.cardNumber.label') }}
                </label>
                <div class="relative input secure-field">
                  <div id="card-number" />
                </div>

                <p v-if="!fields.cardNumber.valid" class="mt-1">
                  <span class="inline-flex px-2 py-1 text-xs leading-none text-white bg-sn-secondary-700">
                    {{ $t('components.payment.error.cardNumber') }}
                  </span>
                </p>
              </div>
            </div>

            <div class="flex w-full">
              <div class="w-48 mr-4">
                <FormInput
                  id="expiryDate"
                  v-model.trim="fields.expiryDate.value"
                  v-mask="'##/##'"
                  :placeholder="$t('components.payment.form.expiryDate.placeholder')"
                  :label="$t('components.payment.form.expiryDate.label')"
                  :valid="fields.expiryDate.valid"
                  :error-string="$t('components.payment.error.expiryDate')"
                  extra-class="pl-3"
                  @focus="resetFieldValidity('expiryDate')"
                />
              </div>

              <div class="w-48">
                <div
                  class="w-full input-component"
                  :class="{ 'is-active': fields.cvv.focused || fields.cvv.filled, 'is-error': !fields.cvv.valid }"
                >
                  <label>
                    {{ $t('components.payment.form.cvv.label') }}
                  </label>
                  <div class="relative input secure-field">
                    <div id="cvv-number" />
                  </div>

                  <p v-if="!fields.cvv.valid" class="mt-1">
                    <span class="inline-flex px-2 py-1 text-xs leading-none text-white bg-sn-secondary-700">
                      {{ $t('components.payment.error.cvv') }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h1 class="pb-2 text-lg font-bold leading-tight xl:text-2xl xl:pb-4">
          {{ $t('components.summary.finalSummary.title') }}
        </h1>

        <div class="w-full mb-4 bg-white border border-sn-gray-200">
          <div class="text-sm xl:text-lg">
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.totalDue') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
              </div>
            </div>
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
              </div>
            </div>
            <div class="flex justify-between p-3 bg-sn-gray-200">
              <div class="mr-3 font-bold">
                {{ $t('components.summary.finalSummary.creditCardAmount') }}
              </div>
              <div class="flex-shrink-0 font-bold font-numeric">
                {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex pt-6">
          <Button
            :loading="loading || secureFieldsProcessing || innerUltimateFailure"
            full-width
            @click="payment"
          >
            {{ $t('components.summary.submitButton') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Payment from '~psp-pages/base/redemption/payment.ts'

import Button from '~brand-components/brussels/button/button.vue'
import FormInput from '~brand-components/brussels/form-input/form-input.vue'
import Loading from '~brand-components/brussels/loading/loading.vue'

export default Payment.extend({
  components: {
    Button,
    FormInput,
    Loading,
  },
  computed: {
    datatransBorder(): string {
      return '#051446'
    },
    datatransBorderError(): string {
      return '#AC0020'
    },
    datatransStyles(): Record<string, string> {
      return {
        '*': `
          width: 100%;
          font-size: 16px;
          color: #051446;
          background-color: transparent;
          height: 3rem;
          border: 1px solid #051446;
          margin: 0;
          padding: 16px 12px 0;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          font-family: "Intelo", sans-serif;
        `,
        '*:focus': `
          outline: 0;
        `,
        '*::placeholder': 'opacity: 0;',
        '*:-ms-input-placeholder': 'opacity: 0;',
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.input-component {
  @apply relative;

  label {
    @apply absolute top-0 left-0 ml-3 pointer-events-none text-sn-gray mt-3;
  }

  .input {
    @apply h-12;

    > div {
      @apply h-12;
    }
  }

  &.is-active {
    label {
      @apply text-xs mt-1;
    }

    .input {
      &::before {
        display: none;
      }
    }
  }

  &.is-error {
    label {
      @apply opacity-100;

      opacity: 1;
    }
    .input {
      input {
        @apply border-error;

        border-color: #c00;
      }
      &::before {
        display: none;
      }
    }
  }
}
</style>
